import * as THREE from "three";

/**
 * Surface detail, drawn at load time on a 2D canvas.
 *
 * Generated rather than shipped: it costs nothing in the bundle, and every
 * pattern stays a few lines of code that can be retuned instead of a binary
 * someone has to open an image editor to change.  These are greyscale detail
 * maps -- the palette still lives in each material's `color`, and the map just
 * multiplies texture into it.
 */
function make(size, draw, repeat = 1, colorSpace = THREE.SRGBColorSpace) {
  // The level modules are also loaded headlessly by tools/inspect.mjs, where
  // there is no canvas to draw on and no renderer that would sample one.
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  draw(g, size);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = colorSpace;
  t.anisotropy = 4;
  t.repeat.set(repeat, repeat);
  return t;
}

const grey = (v) => `rgb(${v | 0},${v | 0},${v | 0})`;

export const planks = () => make(256, (g, s) => {
  g.fillStyle = grey(190); g.fillRect(0, 0, s, s);
  const rows = 5, h = s / rows;
  for (let i = 0; i < rows; i++) {
    g.fillStyle = grey(174 + Math.random() * 34);
    g.fillRect(0, i * h, s, h - 2);
    for (let k = 0; k < 26; k++) {             // grain
      g.strokeStyle = `rgba(90,70,45,${0.05 + Math.random() * 0.07})`;
      g.lineWidth = 1;
      const y = i * h + 3 + Math.random() * (h - 8);
      g.beginPath();
      g.moveTo(Math.random() * s, y);
      g.lineTo(Math.random() * s, y + (Math.random() - 0.5) * 3);
      g.stroke();
    }
    g.fillStyle = "rgba(60,42,26,0.42)";       // seam between planks
    g.fillRect(0, i * h + h - 2, s, 2);
  }
  g.fillStyle = "rgba(60,42,26,0.30)";         // end battens
  g.fillRect(0, 0, 5, s); g.fillRect(s - 5, 0, 5, s);
});

export const stone = () => make(256, (g, s) => {
  g.fillStyle = grey(190); g.fillRect(0, 0, s, s);
  // One value across all three channels.  Rolling each channel separately is
  // how a grey speckle turns into coloured confetti.
  for (let i = 0; i < 1500; i++) {
    const v = Math.random() < 0.5 ? 132 : 236;
    g.fillStyle = `rgba(${v},${v},${v},${0.03 + Math.random() * 0.07})`;
    g.beginPath();
    g.arc(Math.random() * s, Math.random() * s, 0.6 + Math.random() * 2.4, 0, 7);
    g.fill();
  }
  for (let i = 0; i < 26; i++) {                 // hairline cracks
    g.strokeStyle = `rgba(105,105,105,${0.05 + Math.random() * 0.07})`;
    g.lineWidth = 1;
    let x = Math.random() * s, y = Math.random() * s;
    g.beginPath(); g.moveTo(x, y);
    for (let k = 0; k < 5; k++) {
      x += (Math.random() - 0.5) * 34; y += (Math.random() - 0.5) * 34;
      g.lineTo(x, y);
    }
    g.stroke();
  }
});

export const bark = () => make(256, (g, s) => {
  g.fillStyle = grey(182); g.fillRect(0, 0, s, s);
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * s;
    const v = Math.random() < 0.55 ? 116 : 232;
    g.strokeStyle = `rgba(${v},${v},${v},${0.05 + Math.random() * 0.13})`;
    g.lineWidth = 1 + Math.random() * 3.5;
    g.beginPath();
    g.moveTo(x, 0);
    for (let y = 0; y <= s; y += 22) g.lineTo(x + (Math.random() - 0.5) * 7, y);
    g.stroke();
  }
});

export const turf = () => make(256, (g, s) => {
  g.fillStyle = grey(190); g.fillRect(0, 0, s, s);
  for (let i = 0; i < 2600; i++) {
    const v = Math.random() < 0.5 ? 128 : 238;
    g.strokeStyle = `rgba(${v},${v},${v},${0.05 + Math.random() * 0.12})`;
    g.lineWidth = 1;
    const x = Math.random() * s, y = Math.random() * s;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + (Math.random() - 0.5) * 4, y - 2 - Math.random() * 4);
    g.stroke();
  }
});

// --- neighbourhood surfaces -------------------------------------------------
export const brick = () => make(256, (g, s) => {
  g.fillStyle = grey(176); g.fillRect(0, 0, s, s);
  const rows = 10, h = s / rows;
  for (let r = 0; r < rows; r++) {
    const off = (r % 2) * 0.5;
    for (let c = -1; c < 5; c++) {
      const x = (c + off) * (s / 4), y = r * h;
      const v = 186 + Math.random() * 34;
      g.fillStyle = grey(v);
      g.fillRect(x + 1.5, y + 1.5, s / 4 - 3, h - 3);
    }
  }
  g.strokeStyle = "rgba(120,120,120,0.20)";           // mortar shadow
  g.lineWidth = 1;
  for (let r = 0; r <= rows; r++) {
    g.beginPath(); g.moveTo(0, r * h); g.lineTo(s, r * h); g.stroke();
  }
});

export const shingle = () => make(256, (g, s) => {
  g.fillStyle = grey(150); g.fillRect(0, 0, s, s);
  const rows = 8, h = s / rows;
  for (let r = 0; r < rows; r++) {
    const off = (r % 2) * 0.5;
    g.fillStyle = grey(150 + Math.random() * 22);
    g.fillRect(0, r * h, s, h - 1.5);
    g.strokeStyle = "rgba(70,70,70,0.35)";
    g.lineWidth = 1;
    for (let c = -1; c < 7; c++) {                    // tab slots
      const x = (c + off) * (s / 6);
      g.beginPath(); g.moveTo(x, r * h); g.lineTo(x, r * h + h - 2); g.stroke();
    }
    g.fillStyle = "rgba(60,60,60,0.30)";
    g.fillRect(0, r * h + h - 2, s, 2);
  }
});

export const concrete = () => make(256, (g, s) => {
  g.fillStyle = grey(196); g.fillRect(0, 0, s, s);
  for (let i = 0; i < 2400; i++) {
    const v = Math.random() < 0.5 ? 168 : 226;
    g.fillStyle = `rgba(${v},${v},${v},${0.03 + Math.random() * 0.07})`;
    g.beginPath();
    g.arc(Math.random() * s, Math.random() * s, 0.5 + Math.random() * 1.8, 0, 7);
    g.fill();
  }
});

export const asphalt = () => make(256, (g, s) => {
  g.fillStyle = grey(150); g.fillRect(0, 0, s, s);
  for (let i = 0; i < 4200; i++) {
    const v = Math.random() < 0.5 ? 120 : 186;
    g.fillStyle = `rgba(${v},${v},${v},${0.05 + Math.random() * 0.12})`;
    g.fillRect(Math.random() * s, Math.random() * s,
               0.8 + Math.random() * 2.2, 0.8 + Math.random() * 2.2);
  }
});

export const siding = () => make(256, (g, s) => {
  g.fillStyle = grey(196); g.fillRect(0, 0, s, s);
  const rows = 12, h = s / rows;
  for (let r = 0; r < rows; r++) {
    g.fillStyle = grey(190 + Math.random() * 18);
    g.fillRect(0, r * h, s, h - 1);
    g.fillStyle = "rgba(120,120,120,0.28)";
    g.fillRect(0, r * h + h - 1.5, s, 1.5);
  }
});

/**
 * Chain-link fabric, drawn as an alpha mask rather than a colour map: two
 * families of 45-degree lines whose spacing divides the tile exactly, so the
 * diamonds meet themselves at the seam along both axes and a run of any length
 * comes out one continuous mesh under the Builder's world projection.
 *
 * NoColorSpace, which is the only reason make() takes one at all.  A mask read
 * through the sRGB decode the colour maps want is right at 0 and 1 and wrong
 * everywhere between: every antialiased edge texel comes back at about a third
 * of its value, and since the alphaTest cut runs straight through those edges
 * the wire ends up visibly thinner than it was drawn.
 *
 * The wire is drawn about twice life size against the diamond -- 6 mm on a
 * 60 mm mesh, where real fabric is 3 -- and that is deliberate.  alphaTest
 * discards a texel outright, so what survives at range is whatever the mipmap
 * average is, and real chain link averages ~16% opaque: cut at any threshold
 * worth having it vanishes completely a few metres out and the fence becomes a
 * rail floating over grass.  At ~29% the average stays the right side of the
 * 0.25 cut, so the fabric thickens into a haze instead of blinking out --
 * which is roughly what chain link does to the eye at that range anyway.
 */
export const chainLink = () => make(256, (g, s) => {
  g.fillStyle = "#000"; g.fillRect(0, 0, s, s);
  g.strokeStyle = "#fff";
  g.lineWidth = s * 0.028;
  const n = 4;                                   // diamonds across the tile
  const p = s / n;
  // Swept from -n to 2n so the lines that cross in through the left and right
  // edges are drawn too.  A family starting at 0 leaves a bald triangle in two
  // corners of the tile, and that triangle tiles into a hole in the fence.
  for (let k = -n; k <= 2 * n; k++) {
    for (const dir of [1, -1]) {
      g.beginPath();
      g.moveTo(k * p, 0);
      g.lineTo(k * p + dir * s, s);
      g.stroke();
    }
  }
}, 1, THREE.NoColorSpace);

export const paver = () => make(256, (g, s) => {
  g.fillStyle = grey(150); g.fillRect(0, 0, s, s);
  const n = 4, k = s / n;
  for (let a = 0; a < n; a++) {
    for (let b = 0; b < n; b++) {
      g.fillStyle = grey(182 + Math.random() * 40);
      g.fillRect(a * k + 2, b * k + 2, k - 4, k - 4);
    }
  }
});

// --- normal maps ------------------------------------------------------------
/**
 * The same idea as make(), for a map that carries geometry instead of colour.
 *
 * Three things differ from the colour maps above and all of them are
 * load-bearing.  It fills pixels from a height field rather than drawing
 * shapes, because a normal is computed and not painted.  It uses no
 * Math.random, because a normal map has to tile and a field made of dice does
 * not.  And it is tagged NoColorSpace: run a normal map through the sRGB
 * decode the colour maps want and every component is bent toward zero -- x and
 * y hardest, since they sit around 0.5 -- which shows up as a surface that is
 * almost, but not quite, flat everywhere, and no amount of normalScale gets it
 * back.
 *
 * The headless guard is the same one make() carries, for the same reason:
 * tools/inspect.mjs imports the level modules with no document to draw on.
 */
function makeNormal(size, height, strength) {
  if (typeof document === "undefined") return null;
  const h = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) h[y * size + x] = height(x / size, y / size);
  }
  // Wrapping the sample index is what makes the SLOPES tile as well as the
  // heights: take the difference against a clamped edge instead and the seam
  // gets a one-texel crease running the length of it.
  const at = (x, y) => h[(((y % size) + size) % size) * size + (((x % size) + size) % size)];

  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  const img = g.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const du = (at(x + 1, y) - at(x - 1, y)) * 0.5 * size;
      // ImageData row 0 is the top of the canvas and the texture is flipped on
      // upload, so v runs opposite to y.  The green channel's sign comes from
      // that, not from the height field.
      const dv = (at(x, y - 1) - at(x, y + 1)) * 0.5 * size;
      const nx = -du * strength, ny = -dv * strength;
      const k = 255 / (2 * Math.sqrt(nx * nx + ny * ny + 1));
      const i = (y * size + x) * 4;
      img.data[i] = 128 + nx * k;
      img.data[i + 1] = 128 + ny * k;
      img.data[i + 2] = 128 + k;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.NoColorSpace;
  t.anisotropy = 4;
  return t;
}

/**
 * Value noise on a lattice that wraps every `px` by `py` cells.
 *
 * Periodic rather than merely seeded: the lattice corner at cell px is the
 * same corner as cell 0, so the field meets itself at the texture edge with
 * matching values AND matching gradients.  Ordinary noise seamed here whatever
 * the seed, and a seam in a water normal map is a stationary line across a
 * moving surface -- the one thing the eye picks out instantly.
 */
const fade = (t) => t * t * (3 - 2 * t);
function pnoise(x, y, px, py, seed) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = fade(x - xi), yf = fade(y - yi);
  const x0 = ((xi % px) + px) % px, x1 = (x0 + 1) % px;
  const y0 = ((yi % py) + py) % py, y1 = (y0 + 1) % py;
  const hash = (a, b) => {
    let n = (a * 374761393 + b * 668265263 + seed * 1442695041) | 0;
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
  };
  const a = hash(x0, y0), b = hash(x1, y0), c = hash(x0, y1), d = hash(x1, y1);
  const lo = a + (b - a) * xf, hi = c + (d - c) * xf;
  return lo + (hi - lo) * yf;
}

/**
 * The creek's ripples.
 *
 * Three octaves summed, then differenced into a normal.  The octaves are
 * anisotropic -- half as many cells along u as along v, so the features come
 * out twice as long as they are wide -- because an isotropic field reads as a
 * bumpy jelly: moving water is drawn out downstream, and those streaks are
 * most of what says which way it is going.  u is the world x axis once the
 * Builder projects the UVs, which is roughly the creek's line.
 *
 * Cell counts are integers and are also the periods, so every octave tiles at
 * the same texture edge.
 *
 * Two variants, and they have to be two genuinely different fields rather than
 * the same one used twice.  The water crosses them at different speeds and
 * headings to break up the pattern, and two copies of one field crossed with
 * each other still line up completely every time the offsets coincide -- which
 * is a slow, obvious pulse across the whole creek.  Variant 1 is coarser and
 * shallower: it carries the swell the smaller ripples of variant 0 ride on.
 */
const RIPPLE = [
  { octaves: [[3, 6, 1.00, 11], [6, 13, 0.48, 29], [13, 26, 0.24, 71]], strength: 0.030 },
  { octaves: [[2, 5, 1.00, 101], [5, 11, 0.50, 137], [11, 23, 0.26, 181]], strength: 0.024 },
];

export const ripples = (variant = 0) => {
  const { octaves, strength } = RIPPLE[variant % RIPPLE.length];
  return makeNormal(256, (u, v) => {
    let h = 0;
    for (const [fu, fv, amp, seed] of octaves) h += amp * pnoise(u * fu, v * fv, fu, fv, seed);
    return h;
  }, strength);
};
